<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { asA } from '@precon-tools-suite-by-shared/precon-graphql-types';
import { useTagNode } from '@precon-tools-suite-by-shared/shared-tags-data-access';
import {
    ComposedNameSpan,
    EntityNameSpan,
} from '@precon-tools-suite-by-shared/shared-names-ui';
import { TagList } from '@precon-tools-suite-by-shared/shared-tags-ui';
import { usePreconToasts } from '@precon-tools-suite-by-shared/shared-common-toasts-ui';
import {
    NodeShowTitleFragment,
    TagDetailsFragment,
} from './NodeDetailsHeader.generated';
import TagAutoComplete from '../components/TagAutoComplete.vue';
import Button from 'primevue/button';
import OverlayPanel from 'primevue/overlaypanel';
import { utcToLocalTime } from '@precon-tools-suite-by-shared/js-utils';

const props = withDefaults(
    defineProps<{
        node?: NodeShowTitleFragment | null;
        loading?: boolean;
        composedName?: boolean;
        showStamps?: boolean;
        showTags?: boolean;
    }>(),
    {
        node: null,
        loading: false,
        composedName: false,
        showStamps: false,
        showTags: false,
    }
);

const { t } = useI18n();
const { showMessage } = usePreconToasts();
const {
    fetchMoreTagsForNode,
    loading: moreLoading,
    tagNode,
    untagNode,
    mutating,
    onTaggingError,
    onTaggingDone,
    onUntaggingError,
    onUntaggingDone,
} = useTagNode();

const overlayPanel = ref<OverlayPanel | null>();
const tooltipMessage = computed(() => t('AddTag'));

// ------------------------------------------------------------------------------------------------------------------ //
//  STATE                                                                                                             //
// ------------------------------------------------------------------------------------------------------------------ //

const tagsToAdd = ref<string[]>([]);

const typename = computed(() => props?.node?.__typename);

const taggable = computed(() =>
    props.node ? asA(props.node, 'Taggable') : undefined
);

const named = computed(() =>
    props.node ? asA(props.node, 'Named') : undefined
);

const deleted = computed(() => !!asA(props.node, 'SoftDelete')?.deletedAt);
const deletedAt = computed(() => asA(props.node, 'SoftDelete')?.deletedAt);
const deletedBy = computed(
    () =>
        asA(props.node, 'SoftDeleteUserstamped')?.destroyer?.name?.full ??
        t('UnknownUser')
);

const stamped = computed(() =>
    props.node ? asA(props.node, 'Userstamped') : undefined
);

const tags = computed(
    () =>
        taggable.value?.tags?.edges
            ?.map((e) => e?.node)
            ?.filter((n) => !!n)
            ?.map((n) => n as TagDetailsFragment) ?? []
);

const hasMore = computed(() => taggable.value?.tags?.pageInfo?.hasNextPage);
const endCursor = computed(() => taggable.value?.tags?.pageInfo?.endCursor);

const hiddenMessage = 'hi there!';

// ------------------------------------------------------------------------------------------------------------------ //
//  MESSAGES                                                                                                          //
// ------------------------------------------------------------------------------------------------------------------ //

onTaggingDone(() =>
    showMessage({
        messageType: 'update',
        entityTypename: typename.value,
        entityName: named.value?.name?.full,
    })
);

onTaggingError((error) =>
    showMessage({
        messageType: 'error',
        actionType: 'update',
        error: error,
    })
);

onUntaggingDone(() =>
    showMessage({
        messageType: 'update',
        entityTypename: typename.value,
        entityName: named.value?.name?.full,
    })
);

onUntaggingError((error) =>
    showMessage({
        messageType: 'error',
        actionType: 'update',
        error: error,
    })
);

// ------------------------------------------------------------------------------------------------------------------ //
//  ACTIONS                                                                                                           //
// ------------------------------------------------------------------------------------------------------------------ //

const toggleOverlayPanel = (event) => {
    overlayPanel.value?.toggle(event);
};

const onClickMore = () => {
    if (typeof props.node?.id !== 'string') {
        console.warn('Cannot fetch more tags without nodeId');
        return;
    }
    fetchMoreTagsForNode(props.node.id, endCursor.value);
};

const onTagClose = (tag: { id: string }) => {
    if (typeof props.node?.id !== 'string') {
        console.warn('Cannot untag without nodeId');
        return;
    }
    untagNode(props.node.id, tag.id);
};

const onAttachTag = async () => {
    if (typeof props.node?.id !== 'string') {
        console.warn('Cannot tag without nodeId');
        return;
    }
    const tagIds = [...(tagsToAdd.value ?? [])];
    tagsToAdd.value = [];
    await tagNode(props.node.id, tagIds);
};
</script>
<template>
    <div class="flex flex-row">
        <div class="title-and-stamps flex gap-4">
            <h5 v-if="loading || node">
                <span v-if="!loading"
                    >{{ t(typename ?? '-- onbekend type --') }} {{ ' ' }}
                </span>
                <slot name="entityName">
                    <EntityNameSpan
                        v-if="!composedName"
                        :node="named"
                        :loading="loading"
                    />
                    <ComposedNameSpan v-if="composedName" :node="node" />
                </slot>
                <Tag
                    v-if="deleted"
                    v-tooltip.bottom="
                        t('DeletedByAndAt', {
                            name: deletedBy,
                            date: utcToLocalTime(deletedAt),
                        })
                    "
                    :value="t('Deleted')"
                    severity="danger"
                    icon="pi pi-exclamation-triangle"
                />
            </h5>
            <template v-if="showStamps">
                <table>
                    <tr>
                        <td>{{ t('CreatedBy') }}</td>
                        <td>
                            <Skeleton v-if="loading" />
                            <EntityNameSpan
                                v-else
                                :node="stamped?.creator"
                                :loading="loading"
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>{{ t('CreatedAt') }}</td>
                        <td>
                            <Skeleton v-if="loading" />
                            <span v-else>{{
                                utcToLocalTime(stamped?.createdAt)
                            }}</span>
                        </td>
                    </tr>
                </table>
                <table>
                    <tr>
                        <td>
                            <span style="opacity: 0">{{ hiddenMessage }}</span>
                        </td>
                        <td>
                            <span />
                        </td>
                    </tr>
                    <tr v-if="stamped?.isUpdated">
                        <td>{{ t('UpdatedAt') }}</td>
                        <td>
                            <Skeleton v-if="loading" /><span v-else>{{
                                stamped?.updatedAt?.toLocaleString()
                            }}</span>
                        </td>
                    </tr>
                </table>
            </template>
        </div>
        <div class="flex-grow-1" />
        <template v-if="showTags">
            <div class="tags-and-utilities">
                <template v-if="loading">
                    <Skeleton style="min-width: 6rem" />
                </template>
                <div
                    v-else-if="node"
                    class="flex flex-column align-items-end gap-2"
                >
                    <TagList
                        :nodes="tags"
                        :has-more="hasMore"
                        :more-loading="moreLoading"
                        closable
                        @more:click="onClickMore"
                        @tag:close="onTagClose"
                    />
                    <div>
                        <Button
                            v-tooltip.bottom="tooltipMessage"
                            :icon="
                                mutating
                                    ? 'pi pi-spinner pi-spin'
                                    : 'pi pi-plus'
                            "
                            rounded
                            text
                            severity="success"
                            @click="toggleOverlayPanel"
                        />
                        <OverlayPanel ref="overlayPanel">
                            <div class="flex flex-column">
                                <TagAutoComplete v-model="tagsToAdd" />
                                <Button
                                    icon="pi pi-plus"
                                    :label="t('AddTag')"
                                    rounded
                                    text
                                    :disabled="tagsToAdd.length === 0"
                                    severity="success"
                                    @click="onAttachTag"
                                />
                            </div>
                        </OverlayPanel>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
<i18n>
    {
        "en": {
            "CreatedBy": "Created By",
            "CreatedAt": "Created At",
            "UpdatedAt": "Updated At",
            "AddTag": "Add tags",
            "Deleted": "Deleted",
            "DeletedByAndAt": "Deleted by {name}, at {date}",
            "UnknownUser": "Unknown user"
        },
        "nl": {
            "CreatedBy": "Aangemaakt door",
            "CreatedAt": "Aangemaakt op",
            "UpdatedAt": "Bewerkt op",
            "AddTag": "Voeg tags toe",
            "Deleted": "Verwijderd",
            "DeletedByAndAt": "Verwijderd door {name}, op {date}",
            "UnknownUser": "Onbekende gebruiker"
        },
        "fr": {
            "CreatedBy": "Créé par",
            "CreatedAt": "Créé le",
            "UpdatedAt": "Mis à jour le",
            "AddTag": "Add tags",
            "Deleted": "Supprimé",
            "DeletedByAndAt": "Supprimé par {name}, à {date}",
            "UnknownUser": "Utilisateur inconnu"
        }
    }
</i18n>
